import { AlertTriangle, Clock, ArrowUpCircle, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

interface Props {
  trialDaysLeft: number;
  trialExpired: boolean;
  isOnTrial: boolean;
  trialEndsAt: string | null;
  trialStartedAt?: string | null;
  onUpgrade: () => void;
}

const TrialExpiredModal = ({ trialDaysLeft, trialExpired, isOnTrial, trialEndsAt, trialStartedAt, onUpgrade }: Props) => {
  if (!isOnTrial) return null;

  // Calculate total trial days and progress
  const totalTrialDays = trialStartedAt && trialEndsAt
    ? Math.max(1, Math.ceil((new Date(trialEndsAt).getTime() - new Date(trialStartedAt).getTime()) / (1000 * 60 * 60 * 24)))
    : 30;
  const daysUsed = totalTrialDays - trialDaysLeft;
  const progressPercent = Math.min(100, Math.max(0, (daysUsed / totalTrialDays) * 100));

  // Trial banner — urgent when ≤3 days
  if (!trialExpired) {
    const isUrgent = trialDaysLeft <= 3;
    const formattedDate = trialEndsAt
      ? new Date(trialEndsAt).toLocaleDateString("pt-BR", { day: "numeric", month: "long" })
      : "";

    return (
      <div className={`mx-6 mt-4 rounded-xl border p-4 space-y-3 ${
        isUrgent
          ? "border-destructive/40 bg-destructive/5"
          : "border-warning/30 bg-warning/5"
      }`}>
        <div className="flex items-center gap-4">
          {isUrgent ? (
            <Flame className="h-5 w-5 text-destructive shrink-0" />
          ) : (
            <Clock className="h-5 w-5 text-warning shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-semibold ${isUrgent ? "text-destructive" : "text-foreground"}`}>
              {isUrgent
                ? `⚠️ Teste expira em ${trialDaysLeft} ${trialDaysLeft === 1 ? "dia" : "dias"}!`
                : `Período de teste — ${trialDaysLeft} ${trialDaysLeft === 1 ? "dia restante" : "dias restantes"}`
              }
            </p>
            <p className="text-xs text-muted-foreground">
              {isUrgent
                ? `Seu acesso será pausado em ${formattedDate}. Escolha um plano para continuar usando a plataforma.`
                : "Aproveite todos os recursos. Após o teste, escolha um plano para continuar."
              }
            </p>
          </div>
          <Button
            size="sm"
            onClick={onUpgrade}
            className={`shrink-0 ${
              isUrgent
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : "bg-gradient-primary text-primary-foreground"
            }`}
          >
            <ArrowUpCircle className="h-4 w-4 mr-1" />
            {isUrgent ? "Assinar Agora" : "Ver Planos"}
          </Button>
        </div>

        {/* Progress bar */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-[11px] text-muted-foreground">
            <span>{daysUsed} de {totalTrialDays} dias utilizados</span>
            <span className={`font-medium ${isUrgent ? "text-destructive" : "text-foreground"}`}>
              {trialDaysLeft} {trialDaysLeft === 1 ? "dia" : "dias"} restante{trialDaysLeft !== 1 ? "s" : ""}
            </span>
          </div>
          <div className="relative">
            <Progress
              value={progressPercent}
              className={`h-2 ${isUrgent ? "[&>div]:bg-destructive" : "[&>div]:bg-primary"}`}
            />
          </div>
        </div>
      </div>
    );
  }

  // Trial expired — full blocking overlay
  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full p-8 text-center space-y-6">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
          <AlertTriangle className="h-8 w-8 text-destructive" />
        </div>
        <div>
          <h2 className="text-xl font-bold font-display mb-2">Período de teste encerrado</h2>
          <p className="text-sm text-muted-foreground">
            Seu período de teste gratuito expirou. Para continuar utilizando a plataforma,
            escolha um plano que atenda às necessidades do seu negócio.
          </p>
        </div>

        {/* Full progress bar at 100% */}
        <div className="space-y-1.5">
          <div className="flex justify-between items-center text-[11px] text-muted-foreground">
            <span>{totalTrialDays} de {totalTrialDays} dias utilizados</span>
            <span className="font-medium text-destructive">Expirado</span>
          </div>
          <Progress value={100} className="h-2 [&>div]:bg-destructive" />
        </div>

        <Button onClick={onUpgrade} size="lg" className="w-full bg-gradient-primary text-primary-foreground">
          <ArrowUpCircle className="h-5 w-5 mr-2" />
          Escolher um Plano
        </Button>
        <p className="text-[10px] text-muted-foreground">
          Seus dados estão seguros e serão mantidos ao assinar um plano.
        </p>
      </div>
    </div>
  );
};

export default TrialExpiredModal;
